import { useEffect, useState } from 'react';

const SelectClass = ({
  setDisable,
  loading,
  classData,
  setClassData,
  courses,
  loadingCourses,
  clearAttendance,
  batchStructures,
  fetchBatchStructures,
  fetchBatchesByStructure,
}) => {
  const [errors, setErrors] = useState([]);

  const [batches, setBatches] = useState([]);

  /* ================= COURSE ================= */
  const handleCourseChange = async (event) => {
    const courseId = event.target.value;

    setClassData({
      courseId,
      batchStructureId: '',
      batchId: '',
    });

    setBatches([]);
    clearAttendance?.();
    setDisable(true);

    if (courseId) {
      await fetchBatchStructures(courseId);
    }
  };

  /* ================= STRUCTURE ================= */
  const handleStructureChange = async (event) => {
    const batchStructureId = event.target.value;

    setClassData((prev) => ({
      ...prev,
      batchStructureId,
      batchId: '',
    }));

    setBatches([]);
    clearAttendance?.();
    setDisable(true);

    if (batchStructureId) {
      const res = await fetchBatchesByStructure(batchStructureId);
      setBatches(res || []);
    }
  };

  /* ================= BATCH ================= */
  const handleBatchChange = (event) => {
    const batchId = event.target.value;

    setClassData((prev) => ({
      ...prev,
      batchId,
    }));

    clearAttendance?.();
  };

  /* ================= VALIDATION ================= */
  useEffect(() => {
    const newErrors = [];

    if (
      !classData.courseId ||
      !classData.batchStructureId ||
      !classData.batchId
    ) {
      newErrors.push('Please fill in all fields.');
    }

    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [classData, setDisable]);

  return (
    <div className="w-full rounded-lg px-3 pt-7 md:px-8">
      {/* Header */}
      <div className="flex justify-center">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Select Class Information
        </h4>
      </div>

      {loading && (
        <div className="mt-4 text-center">
          <span>Loading...</span>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-center">
        {/* ================= COURSE ================= */}
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Course
          </label>
          <select
            onChange={handleCourseChange}
            value={classData.courseId}
            disabled={loading}
            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Course</option>

            {loadingCourses ? (
              <option>Loading courses...</option>
            ) : (
              courses.map((course, i) => (
                <option key={course._id} value={course._id}>
                  {i + 1}. {course.courseName}
                </option>
              ))
            )}
          </select>
        </div>

        {/* ================= STRUCTURE ================= */}
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch Structure
          </label>
          <select
            onChange={handleStructureChange}
            value={classData.batchStructureId}
            disabled={!classData.courseId}
            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Structure</option>

            {batchStructures.map((structure) => (
              <option key={structure._id} value={structure._id}>
                {structure.name}
              </option>
            ))}
          </select>
        </div>

        {/* ================= BATCH ================= */}
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch
          </label>
          <select
            onChange={handleBatchChange}
            value={classData.batchId}
            disabled={!classData.batchStructureId}
            className="mt-2 w-full md:min-w-[400px] rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Batch</option>

            {loading ? (
              <option>Loading batches...</option>
            ) : batches.length > 0 ? (
              batches.map((b) => {
                const batchDisplay = `B${b.batchNumber}`;
                
                const date = new Date(b.startDate);
                const day = date.getDate().toString().padStart(2, '0');
                const month = date.toLocaleString('default', { month: 'short' });
                const year = date.getFullYear().toString().slice(-2);
                const dateDisplay = `${day}${month}${year}`;

                const dayMap = {
                  Monday: 'M',
                  Tuesday: 'T',
                  Wednesday: 'W',
                  Thursday: 'Th',
                  Friday: 'F',
                  Saturday: 'S',
                  Sunday: 'Su',
                };

                const scheduleDisplay = b.daySchedules
                  ?.map((schedule) => {
                    const dayShort = dayMap[schedule.day] || schedule.day;
                    const time = schedule.startTime.replace(':00', '').replace(' ', '');
                    return `${dayShort}-${time}`;
                  })
                  .join(' | ');

                const branchInitials = b.branches
                  ?.map((br) => br.name.split(',').pop().trim()[0])
                  .join('');

                const schedulePart = scheduleDisplay ? `(${scheduleDisplay} )` : '';
                const fullDisplay = `${batchDisplay} | ${dateDisplay} ${schedulePart} ${branchInitials || ''}`.trim();

                return (
                  <option key={b._id} value={b._id}>
                    {fullDisplay}
                  </option>
                );
              })
            ) : (
              <option>No batches available</option>
            )}
          </select>
        </div>
      </div>

      {/* ================= ERRORS ================= */}
      {errors.length > 0 && (
        <div className="mt-4 text-center text-sm text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectClass;
